import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Variant accent only. The design system ships a light palette and no dark values,
 * so there is no colour-scheme switch (docs/design/design-system.md).
 *
 * `exam` drives `[data-exam]` on <html>, which selects the variant accent tokens.
 * Config-driven: the slug comes from the catalog, never from a branch on exam name.
 */
export type ExamFamilyKey = 'ielts' | 'ielts-general' | 'pte' | 'default';

interface ThemeContextValue {
  readonly exam: ExamFamilyKey;
  setExam: (exam: ExamFamilyKey) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function examKeyFromFamilySlug(slug: string | undefined | null): ExamFamilyKey {
  if (!slug) return 'default';
  const lower = slug.toLowerCase();
  if (lower.includes('general')) return 'ielts-general';
  if (lower.startsWith('ielts')) return 'ielts';
  if (lower.startsWith('pte')) return 'pte';
  return 'default';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [exam, setExam] = useState<ExamFamilyKey>('default');

  useEffect(() => {
    const root = document.documentElement;
    if (exam === 'default') delete root.dataset['exam'];
    else root.dataset['exam'] = exam;
  }, [exam]);

  const value = useMemo(() => ({ exam, setExam }), [exam]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme requires ThemeProvider');
  return ctx;
}
